import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

/**
 * Stop the user leaving a page with unsaved changes.
 *
 * In the app (a link, the back button, a redirect) it holds the navigation
 * for <UnsavedChangesDialog> to confirm; on a reload or a closed tab the
 * browser asks with its own prompt. Staying on the same path - a tab that
 * only changes the query string - is never blocked.
 *
 * Call `allowNavigation()` just before navigating away on purpose, such as
 * after a save, when the page has not yet re-rendered as clean.
 *
 * Needs the data router (createBrowserRouter in App.js) for useBlocker.
 */
export function useUnsavedChangesGuard(dirty) {
  const bypass = useRef(false);

  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        dirty && !bypass.current && currentLocation.pathname !== nextLocation.pathname,
      [dirty],
    ),
  );

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const allowNavigation = useCallback(() => {
    bypass.current = true;
  }, []);

  return { blocker, allowNavigation };
}

/** The confirmation for a navigation the guard held. */
export function UnsavedChangesDialog({ blocker }) {
  return (
    <AlertDialog open={blocker.state === 'blocked'} onOpenChange={(open) => !open && blocker.reset?.()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Leave without saving?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. If you leave this page now, they will be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {/* preventDefault stops the dialog's own close, which would reset() the
              navigation proceed() just released; the dialog closes when the
              blocker leaves 'blocked'. Escape still closes it, and stays. */}
          <AlertDialogCancel
            onClick={(e) => {
              e.preventDefault();
              blocker.reset?.();
            }}
            data-testid="unsaved-stay"
          >
            Keep editing
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              blocker.proceed?.();
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="unsaved-leave"
          >
            Leave without saving
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
